import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";
import { toast } from "sonner";
import { UserPlus, UserCheck, Loader2 } from "lucide-react";

export const FollowButton = ({ authorId, initialFollowing = false, initialCount = 0, compact = false }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [following, setFollowing] = useState(initialFollowing);
  const [count, setCount] = useState(initialCount);
  const [busy, setBusy] = useState(false);

  // Refresh real status when auth changes (e.g. after a like-prompted login)
  useEffect(() => {
    setFollowing(initialFollowing);
    setCount(initialCount);
  }, [initialFollowing, initialCount]);

  useEffect(() => {
    if (!authorId) return;
    api
      .get(`/users/${authorId}/follow-status`)
      .then(({ data }) => {
        setFollowing(!!data.following);
        setCount(data.followers_count || 0);
      })
      .catch(() => { /* ignore */ });
  }, [authorId, user?.user_id]);

  const toggle = async () => {
    if (!user) {
      toast(t("follow_login_required"));
      navigate("/login");
      return;
    }
    if (user.user_id === authorId) return;
    setBusy(true);
    const prev = { following, count };
    setFollowing(!following);
    setCount(count + (following ? -1 : 1));
    try {
      const { data } = await api.post(`/users/${authorId}/follow`);
      setFollowing(!!data.following);
      setCount(data.followers_count || 0);
      toast.success(data.following ? t("follow_success") : t("unfollow_success"));
    } catch {
      setFollowing(prev.following);
      setCount(prev.count);
      toast.error("Failed to update follow");
    } finally {
      setBusy(false);
    }
  };

  // Don't render for own profile
  if (user && user.user_id === authorId) return null;

  const Icon = following ? UserCheck : UserPlus;
  return (
    <button
      onClick={toggle}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 ${compact ? "h-7 px-2 text-[11px]" : "h-9 px-3 text-xs"} border font-mono transition-colors ${
        following
          ? "border-bull/40 text-bull bg-bull/[0.06] hover:bg-bull/[0.10]"
          : "border-white/15 text-neutral-300 hover:border-bull/40 hover:text-bull"
      }`}
      data-testid={`follow-btn-${authorId}`}
      aria-pressed={following}
    >
      {busy ? (
        <Loader2 className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"} animate-spin`} />
      ) : (
        <Icon className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"}`} strokeWidth={2} />
      )}
      {following ? t("follow_following") : t("follow_btn")}
      {!compact && count > 0 && <span className="text-neutral-500">· {count}</span>}
    </button>
  );
};
